/**
 * GolfEngine - Core game logic for the Golf card game.
 *
 * Manages game state including the 2x3 card grid, draw/discard piles,
 * turn logic, knock mechanic, column-pair cancellation scoring,
 * and 9-round game structure.
 */

import {
  Card,
  CardGrid,
  GridCell,
  GridPosition,
  GolfGameState,
  GolfGameStatus,
  PlayerState,
  RoundPhase,
  TurnPhase,
  Scorecard,
  RoundScoreBreakdown,
  ColumnCancellation,
  ReplaceResult,
  getCardValue,
} from './types';
import { createDeck, shuffle, deal } from './DeckManager';

export class GolfEngine {
  private state: GolfGameState;

  constructor() {
    // Initialize with empty default state; call initializeGame() to start
    this.state = this.createEmptyState();
  }

  // ==========================================================================
  // Game Initialization
  // ==========================================================================

  /**
   * Initialize a new game with the specified players.
   * Uses single deck (52 cards) for 2 players, double deck (104 cards) for 3-4 players.
   * Deals 6 cards per player into 2x3 grid (all face-down initially).
   * Sets up draw pile and starts discard pile with top card.
   */
  initializeGame(playerCount: number, playerNames: string[]): void {
    if (playerCount < 2 || playerCount > 4) {
      throw new Error(`Invalid player count: ${playerCount}. Must be 2-4.`);
    }
    if (playerNames.length !== playerCount) {
      throw new Error(`Player names count (${playerNames.length}) must match player count (${playerCount}).`);
    }

    const useDoubleDeck = playerCount > 2;
    let deck = shuffle(createDeck(useDoubleDeck));

    // Deal 6 cards per player into 2x3 grids (all face-down)
    const players: PlayerState[] = [];
    for (let i = 0; i < playerCount; i++) {
      const { dealt, remaining } = deal(deck, 6);
      deck = remaining;

      const grid: CardGrid = [
        [
          { card: dealt[0], faceUp: false },
          { card: dealt[1], faceUp: false },
          { card: dealt[2], faceUp: false },
        ],
        [
          { card: dealt[3], faceUp: false },
          { card: dealt[4], faceUp: false },
          { card: dealt[5], faceUp: false },
        ],
      ];

      players.push({
        grid,
        initialRevealsComplete: false,
        hasKnocked: false,
      });
    }

    // Flip top card from deck to start discard pile
    const { dealt: discardStart, remaining: drawPile } = deal(deck, 1);

    // Set up game state
    this.state = {
      players,
      playerCount,
      playerNames: [...playerNames],
      currentRound: 1,
      totalRounds: 9,
      roundPhase: 'initial-reveal',
      currentPlayerIndex: 0,
      turnPhase: 'draw',
      drawnCard: null,
      drawSource: null,
      knockerIndex: null,
      finalTurnsRemaining: 0,
      drawPile,
      discardPile: discardStart,
      scorecard: this.createEmptyScorecard(playerCount),
      gameMode: 'singleplayer',
      gameStatus: { type: 'initial-reveal' },
      timestamp: Date.now(),
    };
  }

  /**
   * Start a new round while preserving cumulative scores.
   * Resets player grids, piles, and turn state for the next round.
   */
  startNewRound(): void {
    if (this.state.currentRound >= this.state.totalRounds) {
      throw new Error('Cannot start new round: all 9 rounds have been completed.');
    }

    const nextRound = this.state.currentRound + 1;
    const playerCount = this.state.playerCount;
    const useDoubleDeck = playerCount > 2;
    let deck = shuffle(createDeck(useDoubleDeck));

    // Deal 6 cards per player into 2x3 grids (all face-down)
    const players: PlayerState[] = [];
    for (let i = 0; i < playerCount; i++) {
      const { dealt, remaining } = deal(deck, 6);
      deck = remaining;

      const grid: CardGrid = [
        [
          { card: dealt[0], faceUp: false },
          { card: dealt[1], faceUp: false },
          { card: dealt[2], faceUp: false },
        ],
        [
          { card: dealt[3], faceUp: false },
          { card: dealt[4], faceUp: false },
          { card: dealt[5], faceUp: false },
        ],
      ];

      players.push({
        grid,
        initialRevealsComplete: false,
        hasKnocked: false,
      });
    }

    // Flip top card from deck to start discard pile
    const { dealt: discardStart, remaining: drawPile } = deal(deck, 1);

    // Preserve scorecard and cumulative scores
    this.state = {
      ...this.state,
      players,
      currentRound: nextRound,
      roundPhase: 'initial-reveal',
      currentPlayerIndex: 0,
      turnPhase: 'draw',
      drawnCard: null,
      drawSource: null,
      knockerIndex: null,
      finalTurnsRemaining: 0,
      drawPile,
      discardPile: discardStart,
      gameStatus: { type: 'initial-reveal' },
      timestamp: Date.now(),
    };
  }

  // ==========================================================================
  // State Query Methods
  // ==========================================================================

  /**
   * Returns the complete game state.
   */
  getGameState(): GolfGameState {
    return { ...this.state };
  }

  /**
   * Returns the index of the current player.
   */
  getCurrentPlayer(): number {
    return this.state.currentPlayerIndex;
  }

  /**
   * Returns the current round number (1-9).
   */
  getCurrentRound(): number {
    return this.state.currentRound;
  }

  /**
   * Returns the current round phase.
   */
  getRoundPhase(): RoundPhase {
    return this.state.roundPhase;
  }

  /**
   * Returns the number of cards remaining in the draw pile.
   */
  getDrawPileCount(): number {
    return this.state.drawPile.length;
  }

  /**
   * Returns the top card of the discard pile, or null if empty.
   */
  getDiscardPileTop(): Card | null {
    if (this.state.discardPile.length === 0) {
      return null;
    }
    return this.state.discardPile[this.state.discardPile.length - 1];
  }

  /**
   * Returns the card grid for the specified player.
   */
  getPlayerGrid(playerIndex: number): CardGrid {
    if (playerIndex < 0 || playerIndex >= this.state.playerCount) {
      throw new Error(`Invalid player index: ${playerIndex}. Must be 0-${this.state.playerCount - 1}.`);
    }
    return this.state.players[playerIndex].grid;
  }

  /**
   * Returns the scorecard tracking all rounds for all players.
   */
  getScorecard(): Scorecard {
    return this.state.scorecard;
  }

  /**
   * Returns the cumulative scores for all players.
   */
  getCumulativeScores(): number[] {
    return [...this.state.scorecard.cumulativeScores];
  }

  /**
   * Returns whether the game is over (all 9 rounds completed).
   */
  isGameOver(): boolean {
    return this.state.gameStatus.type === 'game-over';
  }

  /**
   * Returns the winner(s) of the game, or null if the game is not over.
   * Returns a single player index, or an array of indices for ties.
   */
  getWinner(): number | number[] | null {
    if (this.state.gameStatus.type !== 'game-over') {
      return null;
    }
    return this.state.gameStatus.winner;
  }

  // ==========================================================================
  // Initial Card Reveal Phase
  // ==========================================================================

  /**
   * Reveal a card during the initial reveal phase.
   * Each player must reveal exactly 2 cards before play begins.
   */
  revealInitialCard(playerIndex: number, position: GridPosition): boolean {
    if (this.state.roundPhase !== 'initial-reveal') {
      return false;
    }
    if (playerIndex < 0 || playerIndex >= this.state.playerCount) {
      return false;
    }
    if (position.row < 0 || position.row > 1 || position.col < 0 || position.col > 2) {
      return false;
    }

    const player = this.state.players[playerIndex];
    if (player.initialRevealsComplete) {
      return false;
    }

    const cell = player.grid[position.row][position.col];
    if (cell.faceUp) {
      return false; // Already revealed
    }

    // Count current reveals for this player
    const currentReveals = this.countFaceUpCards(player.grid);
    if (currentReveals >= 2) {
      return false; // Already revealed 2 cards
    }

    // Reveal the card
    cell.faceUp = true;

    // Check if this player has completed their initial reveals
    if (this.countFaceUpCards(player.grid) >= 2) {
      player.initialRevealsComplete = true;
    }

    // Check if all players have completed initial reveals
    if (this.state.players.every(p => p.initialRevealsComplete)) {
      this.state.roundPhase = 'playing';
      this.state.gameStatus = { type: 'active' };
      this.state.turnPhase = 'draw';
    }

    this.state.timestamp = Date.now();
    return true;
  }

  /**
   * Check if a player has completed their initial card reveal.
   */
  hasCompletedInitialReveal(playerIndex: number): boolean {
    if (playerIndex < 0 || playerIndex >= this.state.playerCount) {
      return false;
    }
    return this.state.players[playerIndex].initialRevealsComplete;
  }

  // ==========================================================================
  // Turn Actions (placeholders for task 4.1)
  // ==========================================================================

  /**
   * Draw a card from the specified source.
   */
  drawCard(source: 'draw-pile' | 'discard-pile'): Card {
    if (this.state.roundPhase !== 'playing' && this.state.roundPhase !== 'final-turns') {
      throw new Error('Cannot draw: not in playing phase.');
    }
    if (this.state.turnPhase !== 'draw') {
      throw new Error('Cannot draw: not in draw phase.');
    }

    let card: Card;

    if (source === 'draw-pile') {
      if (this.state.drawPile.length === 0) {
        this.reshuffleDiscardIntoDraw();
      }
      if (this.state.drawPile.length === 0) {
        throw new Error('Cannot draw: both piles are empty.');
      }
      card = this.state.drawPile.shift()!;
    } else {
      if (this.state.discardPile.length === 0) {
        throw new Error('Cannot draw from empty discard pile.');
      }
      card = this.state.discardPile.pop()!;
    }

    this.state.drawnCard = card;
    this.state.drawSource = source;
    this.state.turnPhase = 'place-or-discard';
    this.state.timestamp = Date.now();

    return card;
  }

  /**
   * Replace a card in the current player's grid with the drawn card.
   */
  replaceCard(position: GridPosition): ReplaceResult {
    if (this.state.turnPhase !== 'place-or-discard') {
      return { success: false, error: 'Cannot replace: not in place-or-discard phase.' };
    }
    if (!this.state.drawnCard) {
      return { success: false, error: 'Cannot replace: no card drawn.' };
    }
    if (position.row < 0 || position.row > 1 || position.col < 0 || position.col > 2) {
      return { success: false, error: 'Invalid grid position.' };
    }

    const player = this.state.players[this.state.currentPlayerIndex];
    const cell = player.grid[position.row][position.col];
    const replacedCard = cell.card;

    // Place drawn card face-up at position
    cell.card = this.state.drawnCard;
    cell.faceUp = true;

    // Put replaced card on discard pile
    this.state.discardPile.push(replacedCard);

    // Clear drawn card state
    this.state.drawnCard = null;
    this.state.drawSource = null;
    this.state.turnPhase = 'complete';
    this.state.timestamp = Date.now();

    // Advance turn
    this.advanceTurn();

    return { success: true, replacedCard };
  }

  /**
   * Discard the drawn card (place it on discard pile).
   * Player must then flip a face-down card.
   */
  discardDrawnCard(): void {
    if (this.state.turnPhase !== 'place-or-discard') {
      throw new Error('Cannot discard: not in place-or-discard phase.');
    }
    if (!this.state.drawnCard) {
      throw new Error('Cannot discard: no card drawn.');
    }

    this.state.discardPile.push(this.state.drawnCard);
    this.state.drawnCard = null;
    this.state.drawSource = null;
    this.state.turnPhase = 'flip';
    this.state.timestamp = Date.now();
  }

  /**
   * Flip a face-down card in the current player's grid (after discarding).
   */
  flipCard(position: GridPosition): boolean {
    if (this.state.turnPhase !== 'flip') {
      return false;
    }
    if (position.row < 0 || position.row > 1 || position.col < 0 || position.col > 2) {
      return false;
    }

    const player = this.state.players[this.state.currentPlayerIndex];
    const cell = player.grid[position.row][position.col];

    if (cell.faceUp) {
      return false; // Must flip a face-down card
    }

    cell.faceUp = true;
    this.state.turnPhase = 'complete';
    this.state.timestamp = Date.now();

    // Advance turn
    this.advanceTurn();

    return true;
  }

  // ==========================================================================
  // Knock Mechanic (placeholders for task 5.1)
  // ==========================================================================

  /**
   * Check if a player can knock (all 6 cards face-up).
   */
  canKnock(playerIndex: number): boolean {
    if (playerIndex < 0 || playerIndex >= this.state.playerCount) {
      return false;
    }
    if (this.state.knockerIndex !== null) {
      return false; // Someone already knocked
    }
    const player = this.state.players[playerIndex];
    return this.countFaceUpCards(player.grid) === 6;
  }

  /**
   * Check if any player has knocked this round.
   */
  hasKnocked(): boolean {
    return this.state.knockerIndex !== null;
  }

  /**
   * Get the index of the player who knocked, or null.
   */
  getKnocker(): number | null {
    return this.state.knockerIndex;
  }

  /**
   * Check if a player is in their final turn (after someone knocked).
   */
  isFinalTurn(playerIndex: number): boolean {
    return this.state.roundPhase === 'final-turns' && this.state.currentPlayerIndex === playerIndex;
  }

  // ==========================================================================
  // Scoring (placeholders for task 6.1)
  // ==========================================================================

  /**
   * Calculate the round score for a player.
   */
  calculateRoundScore(playerIndex: number): RoundScoreBreakdown {
    if (playerIndex < 0 || playerIndex >= this.state.playerCount) {
      throw new Error(`Invalid player index: ${playerIndex}`);
    }

    const player = this.state.players[playerIndex];
    const grid = player.grid;
    const cancellations = this.calculateColumnPairCancellation(grid);
    const cancelledColumns = new Set(cancellations.map(c => c.columnIndex));

    // Calculate card values
    const cardValues: number[][] = [
      [0, 0, 0],
      [0, 0, 0],
    ];

    let subtotal = 0;
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 3; col++) {
        const value = getCardValue(grid[row][col].card);
        cardValues[row][col] = value;
        if (!cancelledColumns.has(col)) {
          subtotal += value;
        }
      }
    }

    // Knock penalty: 10 points if knocker doesn't have strictly lowest score
    const knockPenalty = 0;
    // Knock penalty is applied during round-end scoring in endRound()

    const total = subtotal + knockPenalty;

    return {
      cardValues,
      columnCancellations: Array.from(cancelledColumns),
      subtotal,
      knockPenalty,
      total,
    };
  }

  /**
   * Calculate column-pair cancellations for a grid.
   * Columns where both cards have matching ranks score 0.
   */
  calculateColumnPairCancellation(grid: CardGrid): ColumnCancellation[] {
    const cancellations: ColumnCancellation[] = [];

    for (let col = 0; col < 3; col++) {
      const topCell = grid[0][col];
      const bottomCell = grid[1][col];

      if (topCell.faceUp && bottomCell.faceUp && topCell.card.rank === bottomCell.card.rank) {
        const cancelledValue = getCardValue(topCell.card) + getCardValue(bottomCell.card);
        cancellations.push({
          columnIndex: col,
          matchingRank: topCell.card.rank,
          cancelledValue,
        });
      }
    }

    return cancellations;
  }

  // ==========================================================================
  // Deck Management
  // ==========================================================================

  /**
   * Reshuffle the discard pile (except top card) into the draw pile.
   */
  reshuffleDiscardIntoDraw(): void {
    if (this.state.discardPile.length <= 1) {
      return; // Nothing to reshuffle (keep top card)
    }

    const topCard = this.state.discardPile.pop()!;
    const cardsToShuffle = [...this.state.discardPile];
    this.state.discardPile = [topCard];
    this.state.drawPile = shuffle(cardsToShuffle);
    this.state.timestamp = Date.now();
  }

  // ==========================================================================
  // Serialization (placeholders for task 8.1)
  // ==========================================================================

  /**
   * Serialize the complete game state to JSON.
   */
  serialize(): string {
    return JSON.stringify(this.state);
  }

  /**
   * Deserialize a JSON string into a game state.
   * Returns the game state or an error object.
   */
  deserialize(json: string): GolfGameState | { error: string } {
    try {
      const parsed = JSON.parse(json);
      // Basic structural validation
      if (!parsed || typeof parsed !== 'object') {
        return { error: 'Invalid JSON: not an object.' };
      }
      if (!Array.isArray(parsed.players) || parsed.players.length < 2 || parsed.players.length > 4) {
        return { error: 'Invalid game state: players must be an array of 2-4 elements.' };
      }
      if (typeof parsed.currentRound !== 'number' || parsed.currentRound < 1 || parsed.currentRound > 9) {
        return { error: 'Invalid game state: currentRound must be 1-9.' };
      }
      if (!Array.isArray(parsed.drawPile) || !Array.isArray(parsed.discardPile)) {
        return { error: 'Invalid game state: drawPile and discardPile must be arrays.' };
      }
      // Validate each player's grid
      for (let i = 0; i < parsed.players.length; i++) {
        const player = parsed.players[i];
        if (!player || !Array.isArray(player.grid) || player.grid.length !== 2) {
          return { error: `Invalid game state: player ${i} grid must have 2 rows.` };
        }
        for (let row = 0; row < 2; row++) {
          if (!Array.isArray(player.grid[row]) || player.grid[row].length !== 3) {
            return { error: `Invalid game state: player ${i} grid row ${row} must have 3 columns.` };
          }
        }
      }
      return parsed as GolfGameState;
    } catch (e) {
      return { error: `Failed to parse JSON: ${(e as Error).message}` };
    }
  }

  /**
   * Restore game state from a previously serialized state.
   */
  setGameState(state: GolfGameState): void {
    this.state = { ...state };
  }

  // ==========================================================================
  // Private Helpers
  // ==========================================================================

  /**
   * Advance to the next player's turn.
   * Handles auto-knock detection and final turns tracking.
   */
  private advanceTurn(): void {
    const currentPlayer = this.state.players[this.state.currentPlayerIndex];

    // Check for auto-knock: all 6 cards face-up
    if (this.state.knockerIndex === null && this.countFaceUpCards(currentPlayer.grid) === 6) {
      this.state.knockerIndex = this.state.currentPlayerIndex;
      currentPlayer.hasKnocked = true;
      this.state.roundPhase = 'final-turns';
      this.state.finalTurnsRemaining = this.state.playerCount - 1;
    }

    // Advance to next player
    if (this.state.roundPhase === 'final-turns') {
      this.state.finalTurnsRemaining--;
      if (this.state.finalTurnsRemaining <= 0) {
        // All final turns complete — proceed to scoring
        this.endRound();
        return;
      }
    }

    // Move to next player
    this.state.currentPlayerIndex = (this.state.currentPlayerIndex + 1) % this.state.playerCount;

    // Skip the knocker during final turns
    if (this.state.roundPhase === 'final-turns' && this.state.currentPlayerIndex === this.state.knockerIndex) {
      this.state.currentPlayerIndex = (this.state.currentPlayerIndex + 1) % this.state.playerCount;
    }

    this.state.turnPhase = 'draw';
  }

  /**
   * End the current round: reveal all cards, calculate scores, advance round.
   */
  private endRound(): void {
    // Reveal all remaining face-down cards
    for (const player of this.state.players) {
      for (const row of player.grid) {
        for (const cell of row) {
          cell.faceUp = true;
        }
      }
    }

    // Calculate scores for all players
    const roundScores: RoundScoreBreakdown[] = [];
    for (let i = 0; i < this.state.playerCount; i++) {
      const score = this.calculateRoundScore(i);
      roundScores.push(score);
    }

    // Apply knock penalty if knocker doesn't have strictly lowest score
    if (this.state.knockerIndex !== null) {
      const knockerScore = roundScores[this.state.knockerIndex].subtotal;
      const otherScores = roundScores
        .filter((_, idx) => idx !== this.state.knockerIndex)
        .map(s => s.subtotal);
      const lowestOther = Math.min(...otherScores);

      if (knockerScore >= lowestOther) {
        roundScores[this.state.knockerIndex].knockPenalty = 10;
        roundScores[this.state.knockerIndex].total = roundScores[this.state.knockerIndex].subtotal + 10;
      }
    }

    // Record round scores in scorecard
    const roundIndex = this.state.currentRound - 1;
    for (let i = 0; i < this.state.playerCount; i++) {
      this.state.scorecard.roundScores[i][roundIndex] = roundScores[i];
      this.state.scorecard.cumulativeScores[i] += roundScores[i].total;
    }

    // Check if game is over
    if (this.state.currentRound >= this.state.totalRounds) {
      this.state.roundPhase = 'round-complete';
      this.determineWinner();
    } else {
      this.state.roundPhase = 'round-complete';
      this.state.gameStatus = { type: 'round-end', roundNumber: this.state.currentRound };
    }

    this.state.timestamp = Date.now();
  }

  /**
   * Determine the winner(s) of the game based on lowest cumulative score.
   */
  private determineWinner(): void {
    const scores = this.state.scorecard.cumulativeScores;
    const minScore = Math.min(...scores);
    const winners = scores
      .map((score, index) => ({ score, index }))
      .filter(({ score }) => score === minScore)
      .map(({ index }) => index);

    const winner = winners.length === 1 ? winners[0] : winners;
    this.state.gameStatus = {
      type: 'game-over',
      winner,
      finalScores: [...scores],
    };
  }

  /**
   * Count the number of face-up cards in a grid.
   */
  private countFaceUpCards(grid: CardGrid): number {
    let count = 0;
    for (const row of grid) {
      for (const cell of row) {
        if (cell.faceUp) {
          count++;
        }
      }
    }
    return count;
  }

  /**
   * Create an empty scorecard for the given number of players.
   */
  private createEmptyScorecard(playerCount: number): Scorecard {
    const roundScores: (RoundScoreBreakdown | null)[][] = [];
    const cumulativeScores: number[] = [];

    for (let i = 0; i < playerCount; i++) {
      roundScores.push(new Array(9).fill(null));
      cumulativeScores.push(0);
    }

    return { roundScores, cumulativeScores };
  }

  /**
   * Create an empty default state (used before initializeGame is called).
   */
  private createEmptyState(): GolfGameState {
    return {
      players: [],
      playerCount: 0,
      playerNames: [],
      currentRound: 0,
      totalRounds: 9,
      roundPhase: 'initial-reveal',
      currentPlayerIndex: 0,
      turnPhase: 'draw',
      drawnCard: null,
      drawSource: null,
      knockerIndex: null,
      finalTurnsRemaining: 0,
      drawPile: [],
      discardPile: [],
      scorecard: { roundScores: [], cumulativeScores: [] },
      gameMode: 'singleplayer',
      gameStatus: { type: 'waiting-for-players' },
      timestamp: Date.now(),
    };
  }
}
